'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:qogita-web-sellers":true});}catch(e){}}();
import { type OutlineIconName } from '@qogita/svg-icons'
import { cva } from 'class-variance-authority'
import {
  ComponentProps,
  createContext,
  ReactNode,
  startTransition,
  useContext,
  useState,
} from 'react'

import { cn } from '../../utils/cn'
import { BaseAnchor } from './base-anchor'
import { BaseButton } from './base-button'
import { Icon } from './icon'
import { Separator } from './separator'

/**
 * The semantic meaning of the notification.
 *
 * Controls the color and ARIA role. Use:
 * - `'neutral'` for generic, low-stakes information
 * - `'info'` for informational messages
 * - `'caution'` for warnings that require attention
 * - `'success'` for successful outcomes
 * - `'error'` for problems that block progress (announced as an alert)
 */
export type InlineNotificationTheme =
  | 'neutral'
  | 'info'
  | 'caution'
  | 'success'
  | 'error'

/**
 * Visual weight of the notification.
 *
 * - `'low'` renders a single-line, lightweight message
 * - `'medium'` renders a title plus message for higher emphasis
 */
export type InlineNotificationEmphasis = 'low' | 'medium'

/**
 * Props shared by all inline notification components
 */
type SharedProps = {
  theme: InlineNotificationTheme
  /**
   * Omit for the theme-default icon, pass an outline icon to override, or `null` to hide.
   */
  icon?: OutlineIconName | null
  /** Pass in InlineNotificationActionButton components to render actions */
  actions?: ReactNode
  /** Pass a className to control things like grid placement from the component's parent */
  className?: string
  'data-testid'?: string
}

export type InlineNotificationProps =
  | LowEmphasisInlineNotificationProps
  | MediumEmphasisInlineNotificationProps

export function InlineNotification(props: InlineNotificationProps) {
  switch (props.emphasis) {
    case 'low':
      return <LowEmphasisInlineNotification {...props} />
    case 'medium':
      return <MediumEmphasisInlineNotification {...props} />
  }
}
InlineNotification.displayName = 'InlineNotification'

type LowEmphasisInlineNotificationProps = SharedProps & {
  emphasis: 'low'
  /**
   * String message is preferred. Only use a ReactNode if you need an inline link or other inline element.
   * Don't use this type of notification for complex content, use a medium emphasis notification instead.
   */
  message: string | ReactNode
} & DismissibleProps
function LowEmphasisInlineNotification(
  props: LowEmphasisInlineNotificationProps,
) {
  const { isDismissed, dismiss } = useDismissible(props)

  if (isDismissed) return null

  return (
    <NotificationWrapper
      theme={props.theme}
      emphasis={props.emphasis}
      className={cn('@container flex items-start gap-4', props.className)}
      data-testid={props['data-testid']}
    >
      {props.icon === null ? null : (
        <NotificationIcon
          theme={props.theme}
          icon={props.icon}
          className="translate-y-[3px]" // Our icons don't visually align with the text baseline so this magic number nudges them into alignment
        />
      )}
      <div className="grow @3xl:flex @3xl:justify-between @3xl:gap-4">
        <NotificationMessage className="mr-2 inline font-normal">
          {props.message}
        </NotificationMessage>
        <div className="inline-flex h-min gap-4 empty:hidden">
          {props.actions}
          {props.dismissible && props.actions ? (
            <Separator orientation="vertical" className="@max-3xl:hidden" />
          ) : null}
        </div>
      </div>
      {props.dismissible ? (
        <NotificationDismissButton dismissAction={dismiss} />
      ) : null}
    </NotificationWrapper>
  )
}

type MediumEmphasisInlineNotificationProps = SharedProps & {
  emphasis: 'medium'
  /**
   * Short heading for the notification, shown above the message.
   *
   * Prefer a plain string. Pass a `ReactNode` only for inline text formatting within
   * the title sentence — e.g. `<Money />` or `<LocalDate />`. "Text formatting"
   * means formatting values such as money, dates, or numbers — not styling with
   * `className`s, typography, or colors. Do not use it for block-level elements
   * or layout structure.
   */
  title: string | ReactNode
  message: ReactNode
} & DismissibleProps
function MediumEmphasisInlineNotification(
  props: MediumEmphasisInlineNotificationProps,
) {
  const { isDismissed, dismiss } = useDismissible(props)

  if (isDismissed) return null

  return (
    <NotificationWrapper
      theme={props.theme}
      emphasis={props.emphasis}
      className={cn('flex flex-col gap-1', props.className)}
      data-testid={props['data-testid']}
    >
      <div className="flex items-baseline gap-4">
        {props.icon === null ? null : (
          <NotificationIcon
            theme={props.theme}
            icon={props.icon}
            className="translate-y-[3px]" // Our icons don't visually align with the text baseline so this magic number nudges them into alignment
          />
        )}
        <NotificationTitle className="grow font-medium">
          {props.title}
        </NotificationTitle>
        {props.dismissible ? (
          <NotificationDismissButton dismissAction={dismiss} />
        ) : null}
      </div>
      <NotificationMessage className="font-light">
        {props.message}
      </NotificationMessage>
      <div className="flex flex-wrap gap-4 empty:hidden">{props.actions}</div>
    </NotificationWrapper>
  )
}

type DismissibleProps =
  | {
      dismissible?: false
      isDismissed?: never
      dismissAction?: never
    }
  | {
      dismissible: true
      isDismissed?: boolean
      dismissAction?: () => void
    }

/**
 * Handles controlled or uncontrolled dismissal of a notification
 */
function useDismissible(props: DismissibleProps) {
  const [localIsDismissed, setLocalIsDismissed] = useState(false)

  if (!props.dismissible) {
    return { isDismissed: false, dismiss: () => {} }
  }
  const { isDismissed: controlledIsDismissed, dismissAction } = props

  const isControlledDismissible = controlledIsDismissed !== undefined

  const isDismissed = isControlledDismissible
    ? controlledIsDismissed
    : localIsDismissed

  function dismiss() {
    setLocalIsDismissed(true)
    dismissAction?.()
  }

  return { isDismissed, dismiss }
}

const NotificationContext = createContext<{
  theme: InlineNotificationTheme
  emphasis: 'low' | 'medium'
} | null>(null)
function useNotificationContext() {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error(
      'useNotificationContext must be used within a NotificationWrapper',
    )
  }
  return context
}

const colorVariants = cva('', {
  variants: {
    theme: {
      neutral: 'bg-semantic-neutral text-semantic-neutral-default',
      info: 'bg-semantic-info text-semantic-info-default',
      caution: 'bg-semantic-caution text-semantic-caution-default',
      success: 'bg-semantic-success text-semantic-success-default',
      error: 'bg-semantic-error text-semantic-error-default',
    },
  },
})
const roleMap: Record<InlineNotificationTheme, 'alert' | 'status'> = {
  neutral: 'status', // Status creates a passive live region which is not disruptive, so we use it for most notifications
  info: 'status',
  caution: 'status',
  success: 'status',
  error: 'alert', // Alert creates an assertive live region which is disruptive, so we only use it for errors
}

function NotificationWrapper({
  children,
  emphasis,
  theme,
  className,
  'data-testid': testId,
}: {
  children: ReactNode
  emphasis: 'low' | 'medium'
  theme: InlineNotificationTheme
  className?: string
  'data-testid'?: string
}) {
  const role = roleMap[theme]

  return (
    <NotificationContext value={{ theme, emphasis }}>
      <div
        role={role}
        className={cn(
          'rounded-lg px-4 py-3',
          colorVariants({ theme }),
          className,
        )}
        data-testid={testId}
      >
        {children}
      </div>
    </NotificationContext>
  )
}

/**
 * Renders a paragraph if children is just a string,
 * otherwise renders children in a div
 */
function NotificationMessage({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  const Component = typeof children === 'string' ? 'p' : 'div'
  return <Component className={className}>{children}</Component>
}

function NotificationTitle({
  children,
  className,
}: {
  children: string | ReactNode
  className?: string
}) {
  return <p className={className}>{children}</p>
}

const iconMap: Record<InlineNotificationTheme, OutlineIconName> = {
  neutral: 'heroicons/outline/exclamation-circle',
  info: 'heroicons/outline/information-circle',
  caution: 'heroicons/outline/exclamation-circle',
  success: 'heroicons/outline/check-circle',
  error: 'heroicons/outline/exclamation-triangle',
}
function NotificationIcon({
  theme,
  icon = iconMap[theme],
  className,
}: {
  theme: InlineNotificationTheme
  icon?: OutlineIconName
  className?: string
}) {
  return (
    <Icon name={icon ?? iconMap[theme]} className={cn('size-4.5', className)} />
  )
}

const actionStyleVariants = cva('', {
  variants: {
    theme: {
      neutral: 'hover:text-semantic-neutral-bright',
      info: 'hover:text-semantic-info-bright',
      caution: 'hover:text-semantic-caution-bright',
      success: 'hover:text-semantic-success-bright',
      error: 'hover:text-semantic-error-bright',
    },
    emphasis: {
      low: '',
      medium: 'pt-1 pb-2',
    },
  },
})

type CommonActionProps = {
  /** Optional leading icon */
  icon?: OutlineIconName
}

export function InlineNotificationActionButton({
  children,
  icon,
  ...props
}: ComponentProps<typeof BaseButton> & CommonActionProps) {
  const { theme, emphasis } = useNotificationContext()
  return (
    <BaseButton
      {...props}
      className={cn(
        'inline-flex items-center gap-2 underline underline-offset-4',
        actionStyleVariants({ theme, emphasis }),
        props.className,
      )}
    >
      {icon ? <Icon name={icon} className="size-4.5" /> : null}
      {children}
    </BaseButton>
  )
}

export function InlineNotificationActionAnchor({
  children,
  icon,
  ...props
}: ComponentProps<typeof BaseAnchor> & CommonActionProps) {
  const { theme, emphasis } = useNotificationContext()
  return (
    <BaseAnchor
      {...props}
      className={cn(
        'inline-flex items-center gap-2 underline underline-offset-4',
        actionStyleVariants({ theme, emphasis }),
        props.className,
      )}
    >
      {icon ? <Icon name={icon} className="size-4.5" /> : null}
      {children}
    </BaseAnchor>
  )
}

function NotificationDismissButton({
  dismissAction,
}: {
  dismissAction: () => void
}) {
  return (
    <BaseButton
      className="inline-grid size-6 place-items-center"
      aria-label="Dismiss"
      onClick={() => {
        startTransition(() => {
          dismissAction()
        })
      }}
    >
      <Icon name="heroicons/outline/x-mark" className="size-4.5" />
    </BaseButton>
  )
}
